import { ApolloProvider as ApolloHookProvider } from '@apollo/react-hooks'
import AtRiskCheck from '@components/AtRiskCheck'
import GlobalModalCheck from '@components/GlobalModalCheck'
import { ModalProvider } from '@components/Modal'
import PageOverlay from '@components/PageOverlay'
import TimezoneCheck from '@components/TimezoneCheck'
import { ToasterProvider } from '@components/Toaster'
import ErrorBoundary from '@hocs/ErrorBoundary'
import AutoRaiseCase from '@layouts/Scheduling/AutoRaiseCase'
import { getRoutingAdapter } from '@shell/adapters/routing'
import { getClient } from '@utils/aws-graphql'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { persistCache } from 'apollo-cache-persist'
import React from 'react'
import { ApolloProvider } from 'react-apollo'
import { Provider } from './Context'
import PortalLayout from './PortalLayout'
import UnsavedModal from './UnsavedModal'
import moment from 'moment-timezone'
import EnrollmentAutoProvistion from '@components/EnrollmentAutoProvision'

interface PageLayoutProps {
  children: React.ReactElement
}

interface PageLayoutState {
  isFormDirty: boolean
  modalOpen: boolean
  client: any
}

class PageLayout extends React.PureComponent<PageLayoutProps, PageLayoutState> {
  private _guardUnsubscribe: (() => void) | null = null
  private _guardResolve: ((allow: boolean) => void) | null = null
  private _routeCompleteUnsubscribe: (() => void) | null = null
  // Instance flag (not React state) — set synchronously before setState so the
  // guard callback always reads a fresh value without stale-closure risk.
  private _guardInFlight = false

  constructor(props: PageLayoutProps) {
    super(props)
    this.state = {
      isFormDirty: false,
      modalOpen: false,
      client: null,
    }
  }

  async componentDidMount() {
    // Bootstrap NProgress listeners — SSR-safe, only runs client-side
    getRoutingAdapter().setupListeners()

    // Register form-dirty guard. Async because it may show the UnsavedModal
    // and await the user's decision before allowing or blocking navigation.
    this._guardUnsubscribe = getRoutingAdapter().registerGuard(() => {
      if (!this.state.isFormDirty) return true
      // Use instance flag (not React state) to detect a guard already in flight.
      // this.state.modalOpen could be stale inside this closure; _guardInFlight is
      // set synchronously so it is always fresh.
      if (this._guardInFlight) return false
      this._guardInFlight = true
      return new Promise<boolean>((resolve) => {
        this._guardResolve = resolve
        this.setState({ modalOpen: true })
      })
    })

    // Reset dirty state after any navigation completes (e.g., user confirmed "Don't Save").
    // Uses adapter.onRouteComplete() so PageLayout has no direct next/router dependency.
    this._routeCompleteUnsubscribe = getRoutingAdapter().onRouteComplete(() => {
      if (this.state.isFormDirty) {
        this.setState({ isFormDirty: false, modalOpen: false })
      }
    })

    const apollo = await getClient()
    this.setState({ client: apollo })

    const cache = new InMemoryCache()

    // TODO: add condition when storage is same as cache to avoid redux-persists errors
    await persistCache({
      cache,
      // @ts-ignore
      storage: window.localStorage,
    })
  }

  componentWillUnmount() {
    // Unsubscribe first so neither the guard nor the routeComplete listener
    // can fire on an unmounted component — both run async continuations.
    this._guardUnsubscribe?.()
    this._routeCompleteUnsubscribe?.()
    // Resolve a pending guard Promise only after listeners are torn down.
    // Guarded: _guardResolve is non-null only when the UnsavedModal is genuinely
    // open (guard in flight). Resolving unconditionally would allow navigation the
    // user explicitly cancelled if the component later unmounts for another reason.
    if (this._guardResolve) {
      this._guardResolve(true)
      this._guardResolve = null
      this._guardInFlight = false
    }
  }

  setFormDirtyState = (isFormDirty) => {
    this.setState({ isFormDirty })
  }

  handleModalDontSave = () => {
    this.setState({ isFormDirty: false, modalOpen: false })
    // Resolve the guard promise with true — the adapter will proceed with navigation
    this._guardResolve?.(true)
    this._guardResolve = null
    this._guardInFlight = false
  }

  handleModalClose = () => {
    this.setState({ modalOpen: false })
    // Resolve the guard promise with false — navigation is blocked
    this._guardResolve?.(false)
    this._guardResolve = null
    this._guardInFlight = false
  }

  configureCustomTz = () => {
    moment.tz.link('America/Cuiaba|America/Chile/Santiago')
  }

  render() {
    const { isFormDirty, modalOpen, client } = this.state
    const { children } = this.props
    this.configureCustomTz()
    const value = {
      isFormDirty,
      setFormDirtyState: this.setFormDirtyState,
    }

    if (!client) {
      return <PageOverlay />
    }

    return (
      <ApolloProvider client={client}>
        <ApolloHookProvider client={client}>
          <Provider value={value}>
            <PortalLayout>
              <EnrollmentAutoProvistion>
                <ToasterProvider>
                  <ModalProvider>
                    <ErrorBoundary>{children}</ErrorBoundary>
                    <AutoRaiseCase />
                    <GlobalModalCheck />
                    <UnsavedModal
                      open={modalOpen}
                      onDontSave={this.handleModalDontSave}
                      onClose={this.handleModalClose}
                    />
                  </ModalProvider>
                </ToasterProvider>
              </EnrollmentAutoProvistion>
            </PortalLayout>
          </Provider>
        </ApolloHookProvider>
      </ApolloProvider>
    )
  }
}

export default PageLayout
